import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import Modal from '../../UI/Modal';
import Button from '../../UI/Button';
import Input from '../../UI/Input';
import currencyService from '../../../services/currency/currencyService';
import {
  stripWholeDigits,
  formatThousandsDots,
  wholeDigitsForParse,
  countDigitsBeforeCaret,
  caretPositionForDigitsLeft,
  formatShopifyPriceForDisplay,
} from '../../../utils/priceFormat';
import '../ProductEditModal/ProductEditModal.css';
import './BulkEditModal.css';

const sanitizeFractionDigits = (raw) => String(raw ?? '').replace(/\D/g, '').slice(0, 2);

const emptyApplyRow = {
  priceLira: '',
  priceKurus: '00',
  priceEuro: '',
  priceCent: '00',
};

function tlToPriceFields(tl, eurRate) {
  const safe = Math.max(0, Math.round(tl * 100) / 100);
  const [tInt, tFrac] = safe.toFixed(2).split('.');
  const fields = {
    priceLira: formatThousandsDots(tInt),
    priceKurus: tFrac,
  };
  if (eurRate) {
    const euro = safe / eurRate;
    const [eInt, eFrac] = euro.toFixed(2).split('.');
    fields.priceEuro = formatThousandsDots(eInt);
    fields.priceCent = eFrac;
  }
  return fields;
}

function rowToTl(row) {
  return parseFloat(`${wholeDigitsForParse(row.priceLira)}.${(row.priceKurus || '00').padEnd(2, '0')}`);
}

function BulkProductPriceFields({
  index,
  eurRate,
  row,
  onRowChange,
  firstEuroRef,
}) {
  const priceLiraRef = useRef(null);
  const internalEuroRef = useRef(null);
  const priceEuroRef = firstEuroRef ?? internalEuroRef;
  const pendingLiraCaretRef = useRef(null);
  const pendingEuroCaretRef = useRef(null);

  const { priceLira, priceKurus, priceEuro, priceCent } = row;

  useLayoutEffect(() => {
    const pos = pendingLiraCaretRef.current;
    if (pos != null && priceLiraRef.current) {
      priceLiraRef.current.setSelectionRange(pos, pos);
      pendingLiraCaretRef.current = null;
    }
  }, [priceLira]);

  useLayoutEffect(() => {
    const pos = pendingEuroCaretRef.current;
    if (pos != null && priceEuroRef.current) {
      priceEuroRef.current.setSelectionRange(pos, pos);
      pendingEuroCaretRef.current = null;
    }
  }, [priceEuro]);

  const handleLiraChange = (e) => {
    const el = e.target;
    const sel = el.selectionStart ?? 0;
    const digitsLeft = countDigitsBeforeCaret(el.value, sel);
    const newFormatted = formatThousandsDots(el.value);
    pendingLiraCaretRef.current = caretPositionForDigitsLeft(newFormatted, digitsLeft);
    const updates = { priceLira: newFormatted };
    if (eurRate) {
      const tl = parseFloat(`${wholeDigitsForParse(el.value)}.${priceKurus || '00'}`);
      if (!isNaN(tl)) {
        const euro = tl / eurRate;
        const [eInt, eFrac] = euro.toFixed(2).split('.');
        updates.priceEuro = formatThousandsDots(eInt);
        updates.priceCent = eFrac;
      }
    }
    onRowChange(index, updates);
  };

  const handleKurusChange = (value) => {
    const v = sanitizeFractionDigits(value);
    const updates = { priceKurus: v };
    if (eurRate) {
      const tl = parseFloat(`${wholeDigitsForParse(priceLira)}.${v || '00'}`);
      if (!isNaN(tl)) {
        const euro = tl / eurRate;
        const [eInt, eFrac] = euro.toFixed(2).split('.');
        updates.priceEuro = formatThousandsDots(eInt);
        updates.priceCent = eFrac;
      }
    }
    onRowChange(index, updates);
  };

  const handleEuroChange = (e) => {
    const el = e.target;
    const sel = el.selectionStart ?? 0;
    const digitsLeft = countDigitsBeforeCaret(el.value, sel);
    const newFormatted = formatThousandsDots(el.value);
    pendingEuroCaretRef.current = caretPositionForDigitsLeft(newFormatted, digitsLeft);
    const updates = { priceEuro: newFormatted };
    if (eurRate) {
      const eur = parseFloat(`${wholeDigitsForParse(el.value)}.${priceCent || '00'}`);
      if (!isNaN(eur)) {
        const tl = eur * eurRate;
        const [tInt, tFrac] = tl.toFixed(2).split('.');
        updates.priceLira = formatThousandsDots(tInt);
        updates.priceKurus = tFrac;
      }
    }
    onRowChange(index, updates);
  };

  const handleCentChange = (value) => {
    const v = sanitizeFractionDigits(value);
    const updates = { priceCent: v };
    if (eurRate) {
      const eur = parseFloat(`${wholeDigitsForParse(priceEuro)}.${v || '00'}`);
      if (!isNaN(eur)) {
        const tl = eur * eurRate;
        const [tInt, tFrac] = tl.toFixed(2).split('.');
        updates.priceLira = formatThousandsDots(tInt);
        updates.priceKurus = tFrac;
      }
    }
    onRowChange(index, updates);
  };

  return (
    <div className="price-currency-block bulk-price-currency-block">
      <div className="price-currency-row">
        <div className="price-box">
          <label className="price-label">Fiyat (Euro)</label>
          <div className="price-inputs">
            <div className="price-input-field price-input-field--whole">
              <Input
                ref={priceEuroRef}
                type="text"
                inputMode="numeric"
                value={priceEuro}
                onChange={handleEuroChange}
                placeholder="0"
                className="price-input-whole"
                size="md"
                disabled={!eurRate}
                autoFocus={!!firstEuroRef}
                autoComplete="off"
              />
            </div>
            <span className="price-separator">,</span>
            <div className="price-input-field price-input-field--fraction">
              <Input
                type="number"
                value={priceCent}
                onChange={(e) => handleCentChange(e.target.value)}
                placeholder="00"
                className="price-input-fraction"
                size="md"
                disabled={!eurRate}
                autoComplete="off"
              />
            </div>
            <span className="currency-symbol">€</span>
          </div>
          {!eurRate && (
            <span className="price-hint">Kur bilgisi henüz yüklenmedi…</span>
          )}
        </div>

        <div className="price-box">
          <label className="price-label">Fiyat (TL)</label>
          <div className="price-inputs">
            <div className="price-input-field price-input-field--whole">
              <Input
                ref={priceLiraRef}
                type="text"
                inputMode="numeric"
                value={priceLira}
                onChange={handleLiraChange}
                placeholder="0"
                className="price-input-whole"
                size="md"
                autoComplete="off"
              />
            </div>
            <span className="price-separator">,</span>
            <div className="price-input-field price-input-field--fraction">
              <Input
                type="number"
                value={priceKurus}
                onChange={(e) => handleKurusChange(e.target.value)}
                placeholder="00"
                className="price-input-fraction"
                size="md"
                autoComplete="off"
              />
            </div>
            <span className="currency-symbol">₺</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const BulkEditModal = ({
  isOpen,
  onClose,
  selectedProducts,
  onSave,
  loading = false
}) => {
  const [bulkPrices, setBulkPrices] = useState([]);
  const [applyRow, setApplyRow] = useState(emptyApplyRow);
  const [eurRate, setEurRate] = useState(null);
  const applyEuroRef = useRef(null);
  const selectedProductsKeyRef = useRef('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    currencyService.getCurrencyRates()
      .then((rates) => {
        if (!cancelled && rates?.EUR?.buying) {
          setEurRate(rates.EUR.buying);
        }
      })
      .catch((err) => {
        console.warn('BulkEditModal: EUR kuru alınamadı', err.message);
      });

    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setBulkPrices([]);
      setApplyRow(emptyApplyRow);
      selectedProductsKeyRef.current = '';
      return;
    }
    if (selectedProducts.length === 0) return;

    const key = selectedProducts.map((p) => p.id).join(',');
    if (key === selectedProductsKeyRef.current) return;

    selectedProductsKeyRef.current = key;
    const rows = selectedProducts.map((product) => {
      const [lira, kurus] = product.variants[0].price.split('.');
      return {
        ...product,
        priceLira: formatThousandsDots(lira),
        priceKurus: kurus || '00',
        priceEuro: '',
        priceCent: '00',
      };
    });
    setBulkPrices(rows);
  }, [selectedProducts, isOpen]);

  useEffect(() => {
    if (!eurRate || !isOpen) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      setBulkPrices((prev) => {
        if (prev.length === 0) return prev;
        return prev.map((row) => {
          const tl = parseFloat(`${wholeDigitsForParse(row.priceLira)}.${row.priceKurus || '00'}`);
          if (isNaN(tl)) return row;
          const euro = tl / eurRate;
          const [eInt, eFrac] = euro.toFixed(2).split('.');
          return {
            ...row,
            priceEuro: formatThousandsDots(eInt),
            priceCent: eFrac,
          };
        });
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [eurRate, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      if (applyEuroRef.current) {
        applyEuroRef.current.focus();
        applyEuroRef.current.select();
      }
    }, 100);
    return () => clearTimeout(t);
  }, [isOpen, eurRate]);

  const onRowChange = (index, updates) => {
    setBulkPrices((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const onApplyRowChange = (_index, updates) => {
    setApplyRow((prev) => ({ ...prev, ...updates }));
  };

  const parseApplyTl = () => {
    if (stripWholeDigits(applyRow.priceLira) === '') return null;
    const tl = rowToTl(applyRow);
    if (isNaN(tl) || tl < 0) return null;
    return tl;
  };

  const applyToAll = (mode) => {
    const amount = parseApplyTl();
    if (amount == null) return;
    setBulkPrices((prev) =>
      prev.map((row) => {
        const current = rowToTl(row);
        const base = isNaN(current) ? 0 : current;
        let next = base;
        if (mode === 'set') next = amount;
        if (mode === 'add') next = base + amount;
        if (mode === 'subtract') next = base - amount;
        return { ...row, ...tlToPriceFields(next, eurRate) };
      })
    );
  };

  const handleSave = () => {
    const payload = bulkPrices.map((p) => ({
      id: p.id,
      variantId: p.variants?.[0]?.id,
      previousPrice: p.variants?.[0]?.price,
      priceLira: wholeDigitsForParse(p.priceLira),
      priceKurus: stripWholeDigits(p.priceKurus).slice(0, 2).padEnd(2, '0'),
    }));
    onSave(payload);
  };

  const handleClose = () => {
    setBulkPrices([]);
    setApplyRow(emptyApplyRow);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Toplu Fiyat Düzenleme (${selectedProducts.length} ürün)`}
      size="mdPlusWide"
    >
      <div className="bulk-edit-container">
        {eurRate && (
          <p className="price-rate-note bulk-rate-note">
            1 € = {eurRate.toFixed(2)} ₺ (sadece TL değeri Shopify'a kaydedilir)
          </p>
        )}

        <div className="bulk-price-apply">
          <p className="price-label">Tüm seçili ürünlere uygula</p>
          <BulkProductPriceFields
            index={0}
            eurRate={eurRate}
            row={applyRow}
            onRowChange={onApplyRowChange}
            firstEuroRef={applyEuroRef}
          />
          <div className="bulk-price-apply-buttons">
            <Button
              variant="primary"
              size="sm"
              onClick={() => applyToAll('set')}
              disabled={parseApplyTl() == null || loading}
            >
              Hepsini bu yap
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => applyToAll('add')}
              disabled={parseApplyTl() == null || loading}
            >
              Hepsine ekle
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => applyToAll('subtract')}
              disabled={parseApplyTl() == null || loading}
            >
              Hepsinden çıkar
            </Button>
          </div>
          <p className="bulk-price-apply-hint">
            Çıkarma 0 ₺’nin altına düşmez. Değişiklikler kaydete basınca Shopify’a gider.
          </p>
        </div>

        <div className="bulk-edit-list">
          {bulkPrices.map((product, index) => (
            <div key={product.id} className="bulk-edit-item">
              <div className="product-info">
                <span className="product-title">{product.title}</span>
                <span className="bulk-current-price">
                  Mevcut: {formatShopifyPriceForDisplay(product.variants[0].price)} ₺
                </span>
              </div>

              <div className="price-edit-section">
                <BulkProductPriceFields
                  index={index}
                  eurRate={eurRate}
                  row={product}
                  onRowChange={onRowChange}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="bulk-actions">
          <Button
            variant="success"
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Güncelleniyor...' : 'Tümünü Kaydet'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            İptal
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkEditModal;
